/**
 * What the grid actually asked the server for, and how long each answer took.
 *
 * A slow jump is hard to describe and easy to measure wrongly — a page can be
 * slow because one request was, or because it made eight. Timing it from
 * outside cannot tell those apart, and reading a request's duration in the
 * network panel does not say which block it was or whether the filter was
 * still being counted.
 *
 * So the grid records every row request it makes: which block, how deep, under
 * which filters, and how long. Nothing is printed while things are quick. When
 * a request is slow the console gets one line, and once the burst settles it
 * gets a table of the whole thing — which is the part worth pasting to someone.
 *
 * `window.sheetDiagnostics()` prints the same table on demand.
 */

export type SheetRequest = {
  /** Milliseconds since the page loaded, so a burst reads in order. */
  at: number;
  ms: number;
  offset: number;
  limit: number;
  /** The filters in words, or `none`. */
  filters: string;
  sort: string | null;
  /** How many rows the whole filtered view holds, as the server counted it. */
  matching: number | null;
  returned: number | null;
  failed?: string;
  /** Abandoned because its rows scrolled out of view before they arrived. */
  cancelled?: boolean;
};

/** Long enough that nobody would call it instant. */
const SLOW_MS = 750;

/** Enough to hold a long scroll without growing without bound. */
const KEEP = 300;

const log: SheetRequest[] = [];
let summaryTimer: ReturnType<typeof setTimeout> | null = null;

declare global {
  interface Window {
    __sheetLog?: SheetRequest[];
    sheetDiagnostics?: () => void;
  }
}

function describe(r: SheetRequest): Record<string, unknown> {
  return {
    'at (ms)': Math.round(r.at),
    'took (ms)': r.ms,
    'first row': r.offset + 1,
    rows: r.limit,
    filters: r.filters,
    sort: r.sort ?? '—',
    'rows in view': r.matching ?? '—',
    returned: r.returned ?? (r.cancelled ? 'cancelled' : r.failed ? `failed: ${r.failed}` : '—'),
  };
}

/**
 * Print what happened, once the requests stop arriving.
 *
 * Grouped and collapsed: a scroll can make a dozen requests and the useful
 * part is the total and the slowest, with the detail there if wanted.
 */
export function printSheetDiagnostics(): void {
  if (log.length === 0) {
    console.log('Sheet: no row requests recorded yet.');
    return;
  }
  const first = log[0];
  const last = log[log.length - 1];
  if (!first || !last) return;
  const span = Math.round(last.at + last.ms - first.at);
  const slowest = log.reduce((a, b) => (b.ms > a.ms ? b : a), first);
  const total = log.reduce((sum, r) => sum + r.ms, 0);

  console.groupCollapsed(
    `Sheet: ${log.length} row request${log.length === 1 ? '' : 's'} over ${span}ms ` +
      `· slowest ${slowest.ms}ms at row ${slowest.offset + 1} · ${Math.round(total)}ms of request time`,
  );
  console.table(log.map(describe));
  console.log('Copy this with: copy(JSON.stringify(window.__sheetLog, null, 1))');
  console.groupEnd();
}

/** Record one request, and say something if it was slow. */
export function recordSheetRequest(r: SheetRequest): void {
  log.push(r);
  if (log.length > KEEP) log.splice(0, log.length - KEEP);
  window.__sheetLog = log;
  window.sheetDiagnostics = printSheetDiagnostics;

  /*
    Every request says so, not only the slow ones.

    Printing only what is slow means a console that stays empty tells you
    nothing: whether the page is fast, whether the logging works, or whether
    the request happened at all. One line each is a handful per scroll, and
    the line is the same either way — only its level changes, so the slow ones
    still stand out in a filtered console.
  */
  const line =
    `Sheet ${r.ms}ms · rows ${r.offset + 1}–${r.offset + r.limit} · ` +
    `filters: ${r.filters}` +
    (r.sort ? ` · sorted by ${r.sort}` : '') +
    (r.matching !== null ? ` · view holds ${r.matching.toLocaleString()}` : '');

  if (r.failed) console.error(`${line} · FAILED: ${r.failed}`);
  else if (r.cancelled) console.log(`${line}  ← cancelled, scrolled past`);
  else if (r.ms >= SLOW_MS) console.warn(`${line}  ← slow`);
  else console.log(line);

  // The burst is over when nothing new has arrived for a moment.
  if (summaryTimer) clearTimeout(summaryTimer);
  summaryTimer = setTimeout(() => {
    summaryTimer = null;
    if (log.some((x) => (x.ms >= SLOW_MS && !x.cancelled) || x.failed)) printSheetDiagnostics();
  }, 600);
}

/** Reset between measurements, so one jump can be timed on its own. */
export function clearSheetLog(): void {
  log.length = 0;
}
